import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Task, CurrencyCode, Subtask, Attachment, Tag } from '../../../types';
import { Paperclip, FileText, Image as ImageIcon, Trash2, Download, Eye, ArrowUpRight } from 'lucide-react';
import { apiCall, useKanbanStore } from '../../../store/useKanbanStore';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskData: Partial<Task>) => void;
  onDelete?: (taskId: string) => void;
  initialTask?: Task | null;
  defaultColumnId?: string;
  canEditTasks?: boolean;
}

type SubtaskMetricField = 'estimatedHours' | 'actualHours' | 'estimatedCost' | 'actualCost';

interface SortableSubtaskItemProps {
  st: Subtask;
  currency: CurrencyCode;
  isMetricsOpen: boolean;
  isEditing: boolean;
  editingSubtaskText: string;
  onToggle: () => void;
  onStartEditing: () => void;
  onEditingTextChange: (value: string) => void;
  onSaveTitle: () => void;
  onCancelEditing: () => void;
  onToggleMetrics: () => void;
  onRemove: () => void;
  onConvertToTask?: () => void;
  onUpdateMetric: (
    category: 'time' | 'cost',
    field: SubtaskMetricField,
    value: string,
  ) => void;
}

const SortableSubtaskItem: React.FC<SortableSubtaskItemProps> = ({
  st,
  currency,
  isMetricsOpen,
  isEditing,
  editingSubtaskText,
  onToggle,
  onStartEditing,
  onEditingTextChange,
  onSaveTitle,
  onCancelEditing,
  onToggleMetrics,
  onRemove,
  onConvertToTask,
  onUpdateMetric,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: st.id });
  const hasTime = Boolean(
    st.time && (st.time.estimatedHours > 0 || st.time.actualHours > 0),
  );
  const hasCost = Boolean(
    st.cost && (st.cost.estimatedCost > 0 || st.cost.actualCost > 0),
  );

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      {...attributes}
      className="p-1.5 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 text-xs"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            type="button"
            {...listeners}
            style={{ touchAction: 'none' }}
            className="p-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-grab active:cursor-grabbing shrink-0"
            title="Drag to reorder subtask"
            aria-label={`Drag to reorder ${st.title}`}
          >
            ⠿
          </button>
          <input
            type="checkbox"
            checked={st.completed}
            onChange={onToggle}
            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0"
          />
          {isEditing ? (
            <input
              type="text"
              value={editingSubtaskText}
              onChange={(e) => onEditingTextChange(e.target.value)}
              onBlur={onSaveTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSaveTitle();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelEditing();
                }
              }}
              autoFocus
              aria-label={`Rename ${st.title}`}
              className="flex-1 min-w-0 px-1.5 py-0.5 border border-blue-400 dark:border-blue-500 bg-white dark:bg-slate-800 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : (
            <button
              type="button"
              onClick={onStartEditing}
              className={`${st.completed ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'} min-w-0 flex-1 truncate text-left hover:text-blue-600 dark:hover:text-blue-400`}
              aria-label={`Rename ${st.title}`}
              title="Click to rename subtask"
            >
              {st.title}
            </button>
          )}
          {(hasTime || hasCost) && (
            <span className="text-[9px] text-slate-400 dark:text-slate-500 whitespace-nowrap shrink-0">
              {hasTime && `⏱ ${(st.time?.estimatedHours || 0) + (st.time?.actualHours || 0)}h`}
              {hasCost && ` ${currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'} ${((st.cost?.estimatedCost || 0) + (st.cost?.actualCost || 0)).toLocaleString()}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={onToggleMetrics}
            className={`p-1 rounded text-[10px] ${isMetricsOpen || hasTime || hasCost ? 'text-blue-600 bg-blue-50 dark:bg-blue-950/40' : 'text-slate-400 hover:text-blue-500'}`}
            title="Set subtask time and cost"
            aria-label={`${isMetricsOpen ? 'Hide' : 'Show'} subtask time and cost fields`}
          >
            ⏱
          </button>
          {onConvertToTask && (
            <button
              type="button"
              onClick={onConvertToTask}
              className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400"
              title="Convert to standalone task"
              aria-label={`Convert ${st.title} to a standalone task`}
            >
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="p-1 text-slate-400 hover:text-red-500 font-bold"
            aria-label={`Remove ${st.title}`}
          >
            ✕
          </button>
        </div>
      </div>

      {isMetricsOpen && (
        <div className="mt-1.5 ml-8 space-y-1">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
            <label className="text-[9px] text-slate-500">
              Est Hrs
              <input
                type="number"
                min="0"
                step="0.5"
                value={st.time?.estimatedHours ?? ''}
                onChange={(e) => onUpdateMetric('time', 'estimatedHours', e.target.value)}
                className="mt-0.5 w-full px-1 py-1 text-[11px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded"
              />
            </label>
            <label className="text-[9px] text-slate-500">
              Act Hrs
              <input
                type="number"
                min="0"
                step="0.5"
                value={st.time?.actualHours ?? ''}
                onChange={(e) => onUpdateMetric('time', 'actualHours', e.target.value)}
                className="mt-0.5 w-full px-1 py-1 text-[11px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded"
              />
            </label>
            <label className="text-[9px] text-slate-500">
              Est Cost
              <input
                type="number"
                min="0"
                step="0.01"
                value={st.cost?.estimatedCost ?? ''}
                onChange={(e) => onUpdateMetric('cost', 'estimatedCost', e.target.value)}
                className="mt-0.5 w-full px-1 py-1 text-[11px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded"
              />
            </label>
            <label className="text-[9px] text-slate-500">
              Act Cost
              <input
                type="number"
                min="0"
                step="0.01"
                value={st.cost?.actualCost ?? ''}
                onChange={(e) => onUpdateMetric('cost', 'actualCost', e.target.value)}
                className="mt-0.5 w-full px-1 py-1 text-[11px] border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded"
              />
            </label>
          </div>
          <p className="text-[9px] text-slate-400 dark:text-slate-500">
            Subtask costs use the parent task currency ({currency}).
          </p>
        </div>
      )}
    </li>
  );
};

export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialTask,
  defaultColumnId,
  canEditTasks = true,
}) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [dueDate, setDueDate] = useState<string>('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low' | ''>('');
  const users = useKanbanStore((state) => state.users);
  const addTask = useKanbanStore((state) => state.addTask);
  
  // Tags
  const [tags, setTags] = useState<Tag[]>([]);
  const [newTagInput, setNewTagInput] = useState('');

  // Cost fields
  const [currency, setCurrency] = useState<CurrencyCode>('GBP');
  const [estimatedCost, setEstimatedCost] = useState<number>(0);
  const [actualCost, setActualCost] = useState<number>(0);

  // Time fields
  const [estimatedHours, setEstimatedHours] = useState<number>(0);
  const [actualHours, setActualHours] = useState<number>(0);

  // Subtasks
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState('');
  const [expandedSubtaskId, setExpandedSubtaskId] = useState<string | null>(null);

  // Attachments & Preview
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<Attachment | null>(null);

  useEffect(() => {
    if (initialTask) {
      setTitle(initialTask.title || '');
      setNotes(initialTask.notes || '');
      setAssigneeUserId(initialTask.assignee?.id || '');
      setDueDate(initialTask.dueDate ? new Date(initialTask.dueDate).toISOString().slice(0, 10) : '');
      setPriority(initialTask.priority || '');

      setTags(initialTask.tags || []);
      setCurrency(initialTask.cost?.currency || 'GBP');
      setEstimatedCost(initialTask.cost?.estimatedCost || 0);
      setActualCost(initialTask.cost?.actualCost || 0);
      setEstimatedHours(initialTask.time?.estimatedHours || 0);
      setActualHours(initialTask.time?.actualHours || 0);
      setSubtasks(initialTask.subtasks || []);
      setEditingSubtaskId(null);
      setEditingSubtaskText('');
      setExpandedSubtaskId(null);
      setAttachments(initialTask.attachments || []);
    } else {
      setTitle('');
      setNotes('');
      setAssigneeUserId('');
      setDueDate('');
      setPriority('');
      setTags([]);
      setCurrency('GBP');
      setEstimatedCost(0);
      setActualCost(0);
      setEstimatedHours(0);
      setActualHours(0);
      setSubtasks([]);
      setEditingSubtaskId(null);
      setEditingSubtaskText('');
      setExpandedSubtaskId(null);
      setAttachments([]);
    }
    setPreviewFile(null);
  }, [initialTask, isOpen]);

  if (!isOpen) return null;

  const handleAddTag = () => {
    const trimmed = newTagInput.trim();
    if (trimmed && !tags.some((tag) => tag.label === trimmed)) {
      setTags([...tags, { id: `tag-${Date.now()}`, label: trimmed, color: '#64748b' }]);
      setNewTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: Tag) => {
    setTags(tags.filter((tag) => tag.id !== tagToRemove.id));
  };

  const handleAddSubtask = () => {
    if (!newSubtaskTitle.trim()) return;
    setSubtasks([
      ...subtasks,
      { id: `st-${Date.now()}`, title: newSubtaskTitle.trim(), completed: false },
    ]);
    setNewSubtaskTitle('');
  };

  const handleToggleSubtask = (id: string) => {
    setSubtasks(
      subtasks.map((st) => (st.id === id ? { ...st, completed: !st.completed } : st))
    );
  };

  const startEditingSubtask = (subtask: Subtask) => {
    setEditingSubtaskId(subtask.id);
    setEditingSubtaskText(subtask.title);
  };

  const saveSubtaskTitle = () => {
    if (!editingSubtaskId) return;

    const trimmedTitle = editingSubtaskText.trim();
    if (trimmedTitle) {
      setSubtasks((currentSubtasks) =>
        currentSubtasks.map((st) =>
          st.id === editingSubtaskId ? { ...st, title: trimmedTitle } : st,
        ),
      );
    }

    setEditingSubtaskId(null);
    setEditingSubtaskText('');
  };

  const cancelEditingSubtask = () => {
    setEditingSubtaskId(null);
    setEditingSubtaskText('');
  };

  const handleSubtaskDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSubtasks((items) => {
      const oldIndex = items.findIndex((st) => st.id === active.id);
      const newIndex = items.findIndex((st) => st.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleRemoveSubtask = (id: string) => {
    setSubtasks(subtasks.filter((s) => s.id !== id));
    if (expandedSubtaskId === id) setExpandedSubtaskId(null);
    if (editingSubtaskId === id) cancelEditingSubtask();
  };

  const handleConvertToTask = (stId: string) => {
    const st = subtasks.find((s) => s.id === stId);
    if (!st) return;
    const columnId = initialTask?.columnId ?? defaultColumnId ?? 'col-1';
    addTask({
      columnId,
      title: st.title,
      notes: '',
      tags: [],
      subtasks: [],
      attachments: [],
      comments: st.notes ?? [],
      ...(st.time && { time: st.time }),
      ...(st.cost && {
        cost: { currency, estimatedCost: st.cost.estimatedCost, actualCost: st.cost.actualCost },
      }),
    });
    handleRemoveSubtask(stId);
  };

  const updateSubtaskMetric = (
    id: string,
    category: 'time' | 'cost',
    field: 'estimatedHours' | 'actualHours' | 'estimatedCost' | 'actualCost',
    value: string,
  ) => {
    const parsedValue = Math.max(0, parseFloat(value) || 0);
    setSubtasks((currentSubtasks) =>
      currentSubtasks.map((st) => {
        if (st.id !== id) return st;

        if (category === 'time') {
          return {
            ...st,
            time: {
              estimatedHours: st.time?.estimatedHours || 0,
              actualHours: st.time?.actualHours || 0,
              [field]: parsedValue,
            },
          };
        }

        return {
          ...st,
          cost: {
            estimatedCost: st.cost?.estimatedCost || 0,
            actualCost: st.cost?.actualCost || 0,
            [field]: parsedValue,
          },
        };
      }),
    );
  };

  const handleFilesAdded = async (files: FileList | File[]) => {
    Array.from(files).forEach(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await apiCall('/api/upload', { method: 'POST', body: formData });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        const newAttachment: Attachment = {
          id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          fileName: data.fileName,
          fileUrl: data.fileUrl,
          fileSize: data.fileSize,
          mimeType: data.mimeType,
          createdAt: data.createdAt || new Date().toISOString(),
        };
        setAttachments((prev) => [...prev, newAttachment]);
      } catch (err) {
        console.error('File upload failed:', err);
        alert('Failed to upload file. Please try again.');
      }
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFilesAdded(e.target.files);
    }
  };

  const handleDropFiles = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
    if (e.dataTransfer.files) {
      handleFilesAdded(e.dataTransfer.files);
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
    if (previewFile?.id === id) {
      setPreviewFile(null);
    }
  };

  const renderFileIcon = (mimeType?: string) => {
    if (!mimeType) return <FileText className="w-4 h-4 text-blue-500 shrink-0" />;
    if (mimeType.startsWith('image/')) {
      return <ImageIcon className="w-4 h-4 text-emerald-500 shrink-0" />;
    }
    if (mimeType.includes('pdf')) {
      return <FileText className="w-4 h-4 text-rose-500 shrink-0" />;
    }
    return <FileText className="w-4 h-4 text-blue-500 shrink-0" />;
  };

  const subtaskIds = subtasks.map((st) => st.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEditTasks || !title.trim()) return;

    onSave({
      title,
      notes,
      dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
      priority: priority || undefined,
      columnId: initialTask?.columnId || defaultColumnId || 'col-1',
      tags,
      assignee: assigneeUserId
        ? (() => {
            const matchingUser = users.find((user) => user.id === assigneeUserId);
            return matchingUser ? { id: matchingUser.id, name: matchingUser.username } : undefined;
          })()
        : undefined,
      subtasks,
      cost: { currency, estimatedCost, actualCost },
      time: { estimatedHours, actualHours },
      attachments,
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 sm:backdrop-blur-sm overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-slate-800 rounded-none sm:rounded-lg shadow-xl w-full sm:w-auto sm:max-w-lg overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col my-auto">
        {/* Header */}
        <div className="p-3 sm:p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900 shrink-0">
          <h3 className="font-bold text-lg">
            {initialTask ? 'Edit Task' : 'New Task'}
          </h3>
          <button 
            type="button"
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-bold text-lg"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form id="task-form" onSubmit={handleSubmit} className="p-3 sm:p-5 space-y-4 text-sm">
          <fieldset disabled={!canEditTasks} className={!canEditTasks ? 'opacity-80' : ''}>
          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Task Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Upgrade router firmware"
              required
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Notes / Description</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add extra details or instructions..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label htmlFor="task-assignee" className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Assignee</label>
            <select
              id="task-assignee"
              value={assigneeUserId}
              onChange={(e) => setAssigneeUserId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Unassigned</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.username}</option>
              ))}
            </select>
          </div>

          {/* Due date and priority */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
            <div>
              <label htmlFor="task-due-date" className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Due date</label>
              <input
                id="task-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <span className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Priority</span>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Task priority">
                {([
                  { value: 'high' as const, label: 'High', selected: 'bg-red-500 border-red-500 text-white', idle: 'border-red-300 text-red-700 dark:border-red-800 dark:text-red-300' },
                  { value: 'medium' as const, label: 'Medium', selected: 'bg-amber-500 border-amber-500 text-white', idle: 'border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-300' },
                  { value: 'low' as const, label: 'Low', selected: 'bg-blue-500 border-blue-500 text-white', idle: 'border-blue-300 text-blue-700 dark:border-blue-800 dark:text-blue-300' },
                ]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPriority(priority === option.value ? '' : option.value)}
                    aria-pressed={priority === option.value}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${priority === option.value ? option.selected : `${option.idle} bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800`}`}
                  >
                    {option.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPriority('')}
                  aria-pressed={priority === ''}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${priority === '' ? 'bg-slate-600 border-slate-600 text-white' : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                >
                  None
                </button>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Tags</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add tag..."
                className="flex-1 px-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-md text-xs font-semibold"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag.id}
                  style={{ backgroundColor: tag.color }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white border border-white/30"
                >
                  {tag.label}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-white/80 hover:text-red-100 font-bold ml-0.5"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Subtasks */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">Subtasks</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newSubtaskTitle}
                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSubtask();
                  }
                }}
                placeholder="Add subtask..."
                className="flex-1 px-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={handleAddSubtask}
                className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-md text-xs font-semibold"
              >
                Add
              </button>
            </div>
            <DndContext collisionDetection={closestCenter} onDragEnd={handleSubtaskDragEnd}>
              <SortableContext items={subtaskIds} strategy={verticalListSortingStrategy}>
                <ul className="space-y-1">
                  {subtasks.map((st) => (
                    <SortableSubtaskItem
                      key={st.id}
                      st={st}
                      currency={currency}
                      isMetricsOpen={expandedSubtaskId === st.id}
                      isEditing={editingSubtaskId === st.id}
                      editingSubtaskText={editingSubtaskText}
                      onToggle={() => handleToggleSubtask(st.id)}
                      onStartEditing={() => startEditingSubtask(st)}
                      onEditingTextChange={setEditingSubtaskText}
                      onSaveTitle={saveSubtaskTitle}
                      onCancelEditing={cancelEditingSubtask}
                      onToggleMetrics={() =>
                        setExpandedSubtaskId(expandedSubtaskId === st.id ? null : st.id)
                      }
                      onRemove={() => handleRemoveSubtask(st.id)}
                      onConvertToTask={initialTask ? () => handleConvertToTask(st.id) : undefined}
                      onUpdateMetric={(category, field, value) =>
                        updateSubtaskMetric(st.id, category, field, value)
                      }
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </div>

          {/* Attachments Section */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                Attachments ({attachments.length})
              </label>
            </div>

            {/* Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
              onDragLeave={() => setIsDraggingFile(false)}
              onDrop={handleDropFiles}
              className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer ${
                isDraggingFile
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40'
                  : 'border-slate-300 dark:border-slate-700 hover:border-slate-400 bg-slate-50 dark:bg-slate-900'
              }`}
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="task-file-upload"
              />
              <label htmlFor="task-file-upload" className="cursor-pointer block text-xs text-slate-600 dark:text-slate-400">
                <span className="font-semibold text-blue-600 dark:text-blue-400">Click to upload</span> or drag and drop files here
              </label>
            </div>

            {/* Attachment List */}
            {attachments.length > 0 && (
              <div className="grid grid-cols-1 gap-2 mt-2 max-h-36 overflow-y-auto pr-1">
                {attachments.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-2 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs shadow-xs"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {renderFileIcon(file.mimeType)}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 dark:text-slate-200 truncate">{file.fileName}</p>
                        <span className="text-[10px] text-slate-400">{(file.fileSize / 1024).toFixed(1)} KB</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => setPreviewFile(file)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors"
                        title="Quick Preview"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={file.fileUrl}
                        download={file.fileName}
                        className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors"
                        title="Download File"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(file.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors"
                        title="Delete File"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* File Preview Modal Drawer */}
            {previewFile && (
              <div className="mt-3 p-3 bg-slate-100 dark:bg-slate-900 rounded-md border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-xs text-slate-700 dark:text-slate-300 truncate">
                    Preview: {previewFile.fileName}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPreviewFile(null)}
                    className="text-slate-400 hover:text-slate-600 text-xs"
                  >
                    ✕ Close Preview
                  </button>
                </div>
                <div className="flex justify-center bg-white dark:bg-slate-950 p-2 rounded max-h-48 overflow-auto">
                  {previewFile.mimeType?.startsWith('image/') ? (
                    <img src={previewFile.fileUrl} alt={previewFile.fileName} className="max-h-40 object-contain rounded" />
                  ) : (
                    <div className="text-center py-6 text-slate-500 text-xs space-y-1">
                      <FileText className="w-8 h-8 mx-auto text-blue-500" />
                      <p>Preview not available for this file type.</p>
                      <a
                        href={previewFile.fileUrl}
                        download={previewFile.fileName}
                        className="text-blue-600 underline font-medium"
                      >
                        Download to view
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Time Management Section */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">Time Tracking (Hours)</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Estimated Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs"
                />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Actual Hours</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={actualHours}
                  onChange={(e) => setActualHours(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs"
                />
              </div>
            </div>
          </div>

          {/* Cost Section */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-2">Financials</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
                  className="w-full px-2 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs"
                >
                  <option value="GBP">GBP (£)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="CAD">CAD ($)</option>
                  <option value="AUD">AUD ($)</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Est. Cost</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={estimatedCost}
                  onChange={(e) => setEstimatedCost(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs"
                />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">Act. Cost</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={actualCost}
                  onChange={(e) => setActualCost(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-md text-xs"
                />
              </div>
            </div>
          </div>
          </fieldset>
        </form>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex flex-wrap gap-2 justify-between items-center shrink-0">
          {canEditTasks && initialTask && onDelete ? (
            <button
              type="button"
              onClick={() => {
                if (confirm('Delete this task?')) {
                  onDelete(initialTask.id);
                  onClose();
                }
              }}
              className="px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded"
            >
              Delete Task
            </button>
          ) : <span />}

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md"
            >
              Cancel
            </button>
            {canEditTasks && <button
              type="submit"
              form="task-form"
              className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm"
            >
              {initialTask ? 'Save Changes' : 'Create Task'}
            </button>}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
